import { z } from 'zod';
import { isoDateSchema, localizedTextSchema, objectIdSchema } from './common.js';
import { goalKeySchema } from './user.js';
/**
 * A fallback glyph per goal, for anywhere a goal needs a face.
 *
 * Here rather than in one app because the mobile player and the portal's cover preview both
 * need it, and two copies of the same nine emoji would drift — the portal would keep showing
 * a picture the app does not.
 *
 * It is a FALLBACK. `goals.iconUrl` is the real artwork slot; it is null on every row today
 * and nothing reads it yet, so this map is what actually renders. Goals are database rows so
 * a tenth needs no release — but its emoji would, which is the argument for eventually
 * wiring iconUrl up.
 */
const EMOJI_BY_KEY = {
    OVERTHINKING: '🧠',
    MANAGE_ANGER: '🔥',
    MOVE_ON_FROM_BREAKUP: '💗',
    RELATIONSHIPS: '🤝',
    GOALS: '🎯',
    WEALTH_MINDSET: '💰',
    CONFIDENCE: '💪',
    SLEEP: '🌙',
    DISCIPLINE: '⚡',
};
/** An unmapped goal gets a neutral spark, so it looks deliberate rather than broken. */
export function goalEmoji(goalKey) {
    return EMOJI_BY_KEY[goalKey] ?? '✨';
}
/**
 * Goals are reference data, not an enum — a tenth goal is a row, not a release.
 *
 * The KEY is the stable identifier that user_goals, journeys.goalKeys[],
 * recommendation_rules and every analytics event reference. It is set once at creation and
 * never changes; the display name is free to change, including per language.
 */
export const adminGoalResponseSchema = z.object({
    id: objectIdSchema,
    key: goalKeySchema,
    name: localizedTextSchema,
    iconUrl: z.string().nullable(),
    order: z.number().int(),
    active: z.boolean(),
    /** How many live journeys are tagged with this goal. */
    journeyCount: z.number().int(),
    /** How many users have chosen it. */
    userCount: z.number().int(),
    createdAt: isoDateSchema,
});
export const createGoalBodySchema = z.object({
    key: goalKeySchema,
    name: localizedTextSchema,
    iconUrl: z.string().url().optional(),
});
/** The key is deliberately absent: changing it would orphan every row that references it. */
export const updateGoalBodySchema = z
    .object({
    name: localizedTextSchema.optional(),
    iconUrl: z.string().url().nullable().optional(),
    active: z.boolean().optional(),
})
    .refine((value) => Object.keys(value).length > 0, { message: 'Nothing to update' });
/** Full ordered list of keys. The server renumbers order to 0..N-1 from this. */
export const reorderGoalsBodySchema = z.object({
    keys: z.array(goalKeySchema).min(1),
});
//# sourceMappingURL=goal.js.map